import { useEffect, useState } from "react";
import {
  AdminApiError,
  type AdminTarget,
  type GatewayInfo,
  type PositionSource,
  type RoundInfo,
  closeRound,
  createRound,
  getCurrentRound,
  listAdminTargets,
  listGateways,
  readyRound,
  recoverTarget,
  resetRound,
  setGatewayEnabled,
  startRound,
} from "../api/admin";
import { getMetrics } from "../api/hunters";
import type { PortalMetrics } from "../types/api";
import "./control.css";

const TOKEN_KEY = "aim-admin-token";

type DraftTarget = {
  targetId: string;
  enabledInRound: boolean;
  positionSource: PositionSource;
  carrierId: string;
  displayAlias: string;
};

function TokenGate({ onReady }: { onReady: (token: string) => void }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setChecking(true);
    setError("");
    try {
      await getCurrentRound(input.trim());
      sessionStorage.setItem(TOKEN_KEY, input.trim());
      onReady(input.trim());
    } catch (err) {
      setError(err instanceof AdminApiError && err.status === 403 ? "Token inválido — se requiere rol admin." : "No se pudo validar el token. ¿La API está disponible?");
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="control control-gate">
      <form onSubmit={submit}>
        <h1>CONTROL DE PARTIDA</h1>
        <p>Acceso interno — requiere token de administrador.</p>
        <input
          type="password"
          placeholder="Token Admin"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          autoFocus
        />
        <button disabled={!input.trim() || checking}>{checking ? "Validando…" : "Entrar"}</button>
        {error && <p className="control-error">{error}</p>}
        <small>El token sólo se guarda en esta pestaña (sessionStorage) — nunca en el código ni en el dispositivo.</small>
      </form>
    </div>
  );
}

export default function RoundControl() {
  const [token, setToken] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  });
  if (!token) return <TokenGate onReady={setToken} />;
  return <ControlPanel token={token} onLogout={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); }} />;
}

function ControlPanel({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [round, setRound] = useState<RoundInfo | null | undefined>(undefined);
  const [targets, setTargets] = useState<AdminTarget[]>([]);
  const [gateways, setGateways] = useState<GatewayInfo[]>([]);
  const [draft, setDraft] = useState<DraftTarget[]>([]);
  const [draftName, setDraftName] = useState("Partida AIM 2026");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [readyErrors, setReadyErrors] = useState<string[]>([]);
  const [metrics, setMetrics] = useState<PortalMetrics | null>(null);
  const [recovererName, setRecovererName] = useState("Control de Partida");

  async function refresh() {
    try {
      const [r, t, g] = await Promise.all([getCurrentRound(token), listAdminTargets(token), listGateways(token)]);
      setRound(r);
      setTargets(t);
      setGateways(g);
    } catch (err) {
      if (err instanceof AdminApiError && err.status === 403) onLogout();
    }
  }

  useEffect(() => {
    void refresh();
    const poll = setInterval(refresh, 5000);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (round && round.status === "LIVE" && round.targets[0]) {
      getMetrics(round.targets[0].targetId).then(setMetrics).catch(() => {});
    } else {
      setMetrics(null);
    }
  }, [round]);

  function toggleDraftTarget(t: AdminTarget) {
    setDraft((old) => {
      if (old.some((d) => d.targetId === t.targetId)) return old.filter((d) => d.targetId !== t.targetId);
      return [...old, { targetId: t.targetId, enabledInRound: true, positionSource: "GATEWAY_FUSION", carrierId: "", displayAlias: "" }];
    });
  }

  function updateDraft(targetId: string, patch: Partial<DraftTarget>) {
    setDraft((old) => old.map((d) => (d.targetId === targetId ? { ...d, ...patch } : d)));
  }

  async function submitCreate() {
    setBusy(true);
    setMessage("");
    try {
      const created = await createRound(
        token,
        draftName.trim(),
        draft.map((d) => ({
          targetId: d.targetId,
          enabledInRound: d.enabledInRound,
          positionSource: d.positionSource,
          carrierId: d.positionSource === "CARRIER" ? d.carrierId.trim() || null : null,
          displayAlias: d.displayAlias.trim() || null,
        })),
      );
      setRound(created);
      setDraft([]);
      setMessage(`Partida ${created.roundId} creada (DRAFT).`);
    } catch (err) {
      setMessage(err instanceof AdminApiError ? String(err.detail) : "Error creando la partida.");
    } finally {
      setBusy(false);
    }
  }

  async function doReady() {
    if (!round) return;
    setBusy(true);
    setReadyErrors([]);
    setMessage("");
    try {
      const updated = await readyRound(token, round.roundId);
      setRound(updated);
      setMessage(`Partida ${updated.roundId} lista (READY).`);
    } catch (err) {
      if (err instanceof AdminApiError && err.status === 422) {
        const detail = err.detail as { reasons?: string[] };
        setReadyErrors(detail.reasons ?? [String(err.detail)]);
      } else {
        setMessage("Error validando la partida.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function doStart() {
    if (!round) return;
    setBusy(true);
    setMessage("");
    try {
      setRound(await startRound(token, round.roundId));
      setMessage("Partida LIVE.");
    } catch (err) {
      setMessage(err instanceof AdminApiError ? String(err.detail) : "Error iniciando la partida.");
    } finally {
      setBusy(false);
    }
  }

  async function doRecover(targetId: string) {
    setBusy(true);
    setMessage("");
    try {
      await recoverTarget(token, targetId, recovererName.trim() || "Control de Partida");
      await refresh();
      setMessage(`${targetId} recuperado.`);
    } catch (err) {
      setMessage(err instanceof AdminApiError ? String(err.detail) : "Error registrando la recuperación.");
    } finally {
      setBusy(false);
    }
  }

  async function doClose() {
    if (!round) return;
    setBusy(true);
    setMessage("");
    try {
      setRound(await closeRound(token, round.roundId));
      setMessage("Partida cerrada.");
    } finally {
      setBusy(false);
    }
  }

  async function doReset() {
    if (!round) return;
    setBusy(true);
    setMessage("");
    try {
      const next = await resetRound(token, round.roundId);
      setRound(next);
      setMessage(`Nueva ronda preparada: ${next.roundId} (DRAFT) — vuelve a marcar READY/INICIAR cuando quieras.`);
    } finally {
      setBusy(false);
    }
  }

  async function toggleGateway(g: GatewayInfo) {
    setBusy(true);
    try {
      const updated = await setGatewayEnabled(token, g.observerId, !g.enabled);
      setGateways((old) => old.map((x) => (x.observerId === g.observerId ? updated : x)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="control">
      <header>
        <h1>CONTROL DE PARTIDA</h1>
        <button className="control-logout" onClick={onLogout}>Salir</button>
      </header>
      {message && <p className="control-message">{message}</p>}

      <section className="control-panel">
        <h2>Partida actual</h2>
        {round === undefined && <p>Cargando…</p>}
        {round === null && <p>No hay ninguna partida preparada todavía. Crea una abajo.</p>}
        {round && (
          <>
            <div className="control-round-header">
              <div>
                <strong>{round.displayName}</strong>
                <span className={"control-badge control-badge-" + round.status}>{round.status}</span>
              </div>
              <code>{round.roundId}</code>
            </div>
            <p className="control-count">
              {round.targets.filter((t) => t.enabledInRound).length} BEACONS EN COMPETENCIA
            </p>
            <table className="control-table">
              <thead>
                <tr><th>Target</th><th>MAC</th><th>Fuente</th><th>Carrier</th><th>Alias</th><th>Habilitado</th></tr>
              </thead>
              <tbody>
                {round.targets.map((t) => (
                  <tr key={t.targetId}>
                    <td>{t.targetId}</td>
                    <td>{t.beaconMac ?? <em>sin MAC</em>}</td>
                    <td>{t.positionSource}</td>
                    <td>{t.carrierId ?? "—"}</td>
                    <td>{t.displayAlias ?? "—"}</td>
                    <td>{t.enabledInRound ? "Sí" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {readyErrors.length > 0 && (
              <ul className="control-errors">{readyErrors.map((r) => <li key={r}>{r}</li>)}</ul>
            )}

            <div className="control-actions">
              {(round.status === "DRAFT" || round.status === "READY") && (
                <button disabled={busy} onClick={doReady}>Marcar READY</button>
              )}
              {round.status === "READY" && (
                <button disabled={busy} className="control-primary" onClick={doStart}>INICIAR PARTIDA</button>
              )}
              {(round.status === "LIVE" || round.status === "RECOVERED") && (
                <button disabled={busy} onClick={doClose}>CERRAR PARTIDA</button>
              )}
              {(round.status === "LIVE" || round.status === "RECOVERED" || round.status === "CLOSED") && (
                <button disabled={busy} onClick={doReset}>RESET / NUEVA RONDA</button>
              )}
            </div>

            {round.status === "LIVE" && (
              <div className="control-live">
                <h3>Estado en vivo</h3>
                {metrics && (
                  <div className="control-metrics">
                    <div><strong>{metrics.activeParticipants}</strong><span>Recuperadores activos</span></div>
                    <div><strong>{metrics.detectionsPerMinute}</strong><span>Detecciones/min</span></div>
                    <div><strong>{metrics.carrierFreshness !== null ? `${Math.round(metrics.carrierFreshness)}s` : "—"}</strong><span>Frescura carrier</span></div>
                    <div><strong>{metrics.activeFixedGateways}</strong><span>Gateways activos</span></div>
                  </div>
                )}
                <div className="control-recover">
                  <input value={recovererName} onChange={(e) => setRecovererName(e.target.value)} placeholder="Recuperado por…" />
                  {round.targets.filter((t) => t.enabledInRound).map((t) => (
                    <button key={t.targetId} disabled={busy} onClick={() => doRecover(t.targetId)}>
                      ✓ Recuperar {t.targetId}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>

      <section className="control-panel">
        <h2>Preparar nueva partida</h2>
        <input value={draftName} onChange={(e) => setDraftName(e.target.value)} placeholder="Nombre de la partida" />
        <p className="control-count">
          {draft.filter((d) => d.enabledInRound).length} BEACONS EN COMPETENCIA
        </p>
        <table className="control-table">
          <thead>
            <tr><th></th><th>Target</th><th>MAC</th><th>Fuente</th><th>Carrier</th><th>Alias</th></tr>
          </thead>
          <tbody>
            {targets.map((t) => {
              const d = draft.find((x) => x.targetId === t.targetId);
              return (
                <tr key={t.targetId}>
                  <td><input type="checkbox" checked={!!d} onChange={() => toggleDraftTarget(t)} /></td>
                  <td>{t.targetId}</td>
                  <td>{t.beaconMac ?? <em>sin MAC — no se puede iniciar sin registrar antes</em>}</td>
                  <td>
                    {d && (
                      <select value={d.positionSource} onChange={(e) => updateDraft(t.targetId, { positionSource: e.target.value as PositionSource })}>
                        <option value="GATEWAY_FUSION">GATEWAY_FUSION</option>
                        <option value="CARRIER">CARRIER</option>
                      </select>
                    )}
                  </td>
                  <td>
                    {d && d.positionSource === "CARRIER" && (
                      <input value={d.carrierId} onChange={(e) => updateDraft(t.targetId, { carrierId: e.target.value })} placeholder="CARRIER-01" />
                    )}
                  </td>
                  <td>
                    {d && (
                      <input value={d.displayAlias} onChange={(e) => updateDraft(t.targetId, { displayAlias: e.target.value })} placeholder="Alias opcional" />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button disabled={busy || !draftName.trim() || draft.length === 0} className="control-primary" onClick={submitCreate}>
          Crear partida (DRAFT)
        </button>
      </section>

      <section className="control-panel">
        <h2>Gateways fijos (infraestructura opcional)</h2>
        <table className="control-table">
          <thead>
            <tr><th>Gateway</th><th>Zona</th><th>Estado</th><th>Último visto</th><th>Habilitado</th></tr>
          </thead>
          <tbody>
            {gateways.map((g) => (
              <tr key={g.observerId}>
                <td>{g.observerId}</td>
                <td>{g.zoneId ?? "—"}</td>
                <td>{g.online ? "● Online" : "○ Offline"}</td>
                <td>{g.lastSeenAt ? new Date(g.lastSeenAt).toLocaleTimeString("es-MX", { hour12: false }) : "—"}</td>
                <td><button disabled={busy} onClick={() => toggleGateway(g)}>{g.enabled ? "Deshabilitar" : "Habilitar"}</button></td>
              </tr>
            ))}
            {!gateways.length && <tr><td colSpan={5}>Sin gateways registrados todavía.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}
